import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Maximize2, Minimize2, X } from 'lucide-react';

interface AttachmentPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachment: {
    id: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    contentUrl: string;
  } | null;
}

const AttachmentPreviewModal: React.FC<AttachmentPreviewModalProps> = ({
  open,
  onOpenChange,
  attachment,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Listen for fullscreen change events from browser controls
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);
  
  // Clean up object URLs when the component is unmounted or preview changes
  React.useEffect(() => {
    // Reset error state when attachment changes
    setError(null);
    
    // Only cleanup when component unmounts or attachment changes
    return () => {
      if (attachment?.contentUrl) {
        // Attempt to revoke the URL to prevent memory leaks
        try {
          URL.revokeObjectURL(attachment.contentUrl);
        } catch (e) {
          console.error('Failed to revoke object URL:', e);
        }
      }
    };
  }, [attachment]);

  // Early return after hooks
  if (!attachment) return null;

  // Get file type from both the type and extension in filename to be more robust
  const getFileType = (att: typeof attachment) => {
    const fileType = att.fileType.toLowerCase();
    const fileName = att.fileName.toLowerCase();
    
    // Check extension in filename as a fallback
    if (fileName.endsWith('.pdf')) return 'pdf';
    if (fileName.match(/\.(jpe?g|png|gif)$/)) return 'image';
    if (fileName.match(/\.(mp4|webm)$/)) return 'video';
    if (fileName.match(/\.(mp3|wav|ogg)$/)) return 'audio';
    
    // Use the provided file type
    if (fileType === 'pdf' || fileType.includes('pdf')) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'image/jpeg', 'image/png', 'image/gif'].some(t => fileType.includes(t))) return 'image';
    if (['mp4', 'webm', 'video/mp4', 'video/webm'].some(t => fileType.includes(t))) return 'video';
    if (['mp3', 'wav', 'ogg', 'audio/mpeg', 'audio/wav'].some(t => fileType.includes(t))) return 'audio';
    
    return 'unknown';
  };
  
  const fileFormat = getFileType(attachment);
  const isImage = fileFormat === 'image';
  const isPdf = fileFormat === 'pdf';
  const isVideo = fileFormat === 'video';
  const isAudio = fileFormat === 'audio';

  const toggleFullscreen = () => {
    const element = document.querySelector('.preview-container');
    if (!element) return;

    if (!isFullscreen) {
      if (element.requestFullscreen) {
        element.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${isFullscreen ? 'fullscreen-dialog' : ''} max-w-5xl w-[90vw] h-[85vh] p-0 m-0 overflow-hidden flex flex-col bg-background dark:bg-background rounded-lg`}>
        {/* Custom header with our own controls only */}
        <div className="flex flex-row items-center justify-between p-3 border-b bg-background dark:bg-background z-10">
          <div className="text-base font-medium truncate max-w-[70%]">{attachment.fileName}</div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Go fullscreen"}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="preview-container flex-1 overflow-hidden flex flex-col bg-muted/30 m-0 p-0">
          {isImage && (
            <div className="flex items-center justify-center h-full w-full p-4">
              <img 
                src={attachment.contentUrl} 
                alt={attachment.fileName} 
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  console.error('Image failed to load:', e);
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement?.insertAdjacentHTML('beforeend', 
                    `<div class="text-center p-4"><p class="text-red-500">Failed to load image</p></div>`);
                }}
              />
            </div>
          )}
          
          {isPdf && (
            <div className="w-full h-full flex items-center justify-center p-4 bg-muted/10">
              {/* Create a custom PDF viewer display */}
              <a 
                href={attachment.contentUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-full w-full flex items-center justify-center"
                onClick={(e) => {
                  // Prevent opening in a new tab if clicked directly
                  e.preventDefault();
                  
                  // Instead create a PDF viewer in the current container
                  const container = e.currentTarget.parentElement;
                  if (!container) return;
                  
                  // Clear container and add PDF object
                  container.innerHTML = '';
                  
                  // Create PDF embed
                  const embed = document.createElement('embed');
                  embed.setAttribute('src', attachment.contentUrl);
                  embed.setAttribute('type', 'application/pdf');
                  embed.setAttribute('width', '100%');
                  embed.setAttribute('height', '100%');
                  embed.style.border = 'none';
                  
                  // Append to container
                  container.appendChild(embed);
                }}
              >
                {/* Initial display shows PDF icon and instructions to click for viewing */}
                <div className="text-center">
                  <svg className="mx-auto h-16 w-16 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-4 text-lg font-medium">{attachment.fileName}</p>
                  <p className="mt-2">Click to view the PDF</p>
                </div>
              </a>
            </div>
          )}
          
          {isVideo && (
            <div className="flex items-center justify-center h-full w-full">
              <video
                src={attachment.contentUrl}
                controls
                controlsList="nodownload"
                className="max-w-full max-h-full"
                onError={(e) => {
                  console.error('Video failed to load:', e);
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement?.insertAdjacentHTML('beforeend', 
                    `<div class="text-center p-4"><p class="text-red-500">Failed to load video</p></div>`);
                }}
              >
                Your browser does not support video playback.
              </video>
            </div>
          )}
          
          {isAudio && (
            <div className="flex flex-col items-center justify-center w-full">
              <div className="text-center mb-4">
                <p className="text-lg font-medium">{attachment.fileName}</p>
              </div>
              <audio
                src={attachment.contentUrl}
                controls
                controlsList="nodownload"
                className="w-full max-w-md"
                onError={(e) => {
                  console.error('Audio failed to load:', e);
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement?.insertAdjacentHTML('beforeend', 
                    `<div class="text-center p-4"><p class="text-red-500">Failed to load audio</p></div>`);
                }}
              >
                Your browser does not support audio playback.
              </audio>
            </div>
          )}
          
          {!isImage && !isPdf && !isVideo && !isAudio && (
            <div className="text-center p-8">
              <p className="text-xl font-semibold mb-2">Preview not available</p>
              <p>The file type cannot be previewed directly.</p>
              <Button
                className="mt-4"
                onClick={() => window.open(attachment.contentUrl, '_blank')}
              >
                Download File
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AttachmentPreviewModal;
